// 261015 P4c-1 — theo dõi job "Tạo tổng quan" (FILM_BUILD_OUTLINE phase 'overview') và GIỮ `detail.result` để nói thẳng những gì bị bỏ/thiếu
// (`droppedManualClaims`, `emptyVolumes`, `missingVolumes`…). Workbench không trả `result`, nên chỉ phiên đang theo dõi job mới thấy — dữ liệu bền
// (quyển rỗng, quyển thiếu) vẫn đọc được từ `overview.source` ở mọi lúc (xem `overviewView.emptyVolumesOf`).
// Đặt ở `SkeletonStage` (luôn mount khi mở ②) chứ không ở tab con, để đổi tab con không mất theo dõi.
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { adaptationApi, type OverviewMode } from '../../../services/api/adaptation';
import type { OverviewJobResult } from '../../../types/adaptation';
import type { WorkbenchRunningJob } from '../../../types/adaptationWorkbench';
import { useFilmJobPoll } from '../useFilmJobPoll';
import { jobFailedToast, friendlyJobNote } from './jobErrorText';
import { pickJobId } from './jobTracking';
import { resultSummary } from './overview/overviewView';

export interface OverviewJobApi {
  /** Job đang theo dõi (do phiên này bấm HOẶC nhận nuôi từ `workbench.jobs.running`). */
  jobId: string | null;
  starting: boolean;
  running: WorkbenchRunningJob | null;
  /** Kết quả job vừa xong ở phiên này (null = chưa có / đã đóng). */
  result: OverviewJobResult | null;
  /** Lỗi của job vừa xong ở phiên này. */
  failure: string | null;
  dismiss: () => void;
  /** Ném lại lỗi API cho nơi gọi (hộp lỗi có mã + lối ra). */
  start: (mode: OverviewMode, expectedModifiedAt?: string) => Promise<boolean>;
}

export const useOverviewJob = (storyId: string, running: ReadonlyArray<WorkbenchRunningJob> | null | undefined, onFinished: () => void): OverviewJobApi => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [result, setResult] = useState<OverviewJobResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const done = useRef<Set<string>>(new Set());
  const candidate = pickJobId(running, 'overview');

  useEffect(() => { setJobId(null); setResult(null); setFailure(null); done.current = new Set(); }, [storyId]);
  useEffect(() => {
    if (!jobId && !starting && candidate && !done.current.has(candidate)) setJobId(candidate);
  }, [jobId, starting, candidate]);

  const start = useCallback(async (mode: OverviewMode, expectedModifiedAt?: string): Promise<boolean> => {
    setStarting(true);
    try {
      const r = await adaptationApi.buildOverview(storyId, { mode, ...(expectedModifiedAt ? { expectedModifiedAt } : {}) });
      if (!r?.jobId) { message.warning('Server không trả job id; đã dừng theo dõi và không tự gửi lại.'); onFinished(); return false; }
      setResult(null); setFailure(null);
      setJobId(r.jobId);
      message.info('Đang tạo tổng quan ở nền — bạn có thể rời màn hình và quay lại', 5);
      return true;
    } finally { setStarting(false); }
  }, [storyId, onFinished]);

  useFilmJobPoll<{ result?: OverviewJobResult; error?: string; note?: string }>(jobId, {
    onTerminal: async (outcome, task) => {
      if (jobId) done.current.add(jobId);
      setJobId(null);
      if (outcome === 'completed') {
        const r = (task?.detail?.result ?? null) as OverviewJobResult | null;
        setResult(r);
        const sum = resultSummary(r);
        if (sum && sum.tone !== 'success') message.warning(sum.title, 6); else message.success('Đã tạo xong tổng quan', 5);
      } else if (outcome === 'failed') {
        const t = jobFailedToast(task?.detail?.error, task?.detail?.note);
        setFailure(friendlyJobNote(t) || t);
        message.error(t, 8);
      } else message.warning('Đã dừng theo dõi — việc có thể vẫn chạy, bấm Tải lại.', 6);
      onFinished();
    },
  });

  const runningJob = (running ?? []).find((j) => j.kind === 'overview') ?? null;
  return { jobId, starting, running: runningJob, result, failure, dismiss: () => { setResult(null); setFailure(null); }, start };
};
